<template>
  <div class="flex justify-between gap-4 p-3 hover:bg-gray-100 cursor-pointer notification">
    <div class="rounded-3xl w-8 h-8 flex justify-center align-items-center" style="background-color: rgba(248, 248, 249, 1)">
      <component
        v-if="getNotificationIcon(inboxNotification.type).component.length > 0"
        :is="getNotificationIcon(inboxNotification.type).component"
        v-bind="getNotificationIcon(inboxNotification.type).props"
        :color="getHighlightColor"
      />
    </div>
    <div class="flex flex-grow flex-col space-y-2 w-10/12"  @click="redirectTo">
      <div class="flex justify-between align-items-center ">
        <WcTypography :title="inboxNotification.title" class="w-8/12" size="xs" weight="600"  :color="inboxNotification.isRead? 'rgb(4,4,4,0.47)':'rgb(4,4,4,0.77)'"/>
        <div class="w-4/12 flex justify-end gap-4 align-items-center" @click.stop>
          <WcTooltip :text="getInboxReadText" >
            <div @click="markAsReadUnread">
              <ElBadge>
                <WcIconMessage :is-opened="inboxNotification.isRead" :color="getHighlightColor" />
              </ElBadge>
            </div>
          </WcTooltip>
          <WcTooltip :text="getStarredText">
            <div @click="handleImportant"><WcIconStar :stroke-color="getHighlightColor" :color="inboxNotification.isImportant ? '#5138ee': ''" /></div>
          </WcTooltip>
          <WcTooltip :text="$t('general.delete')">
            <div @click="handleDeleteNotificationDialog">
              <WcIconBin color="red"/>
            </div>
          </WcTooltip>
        </div>
      </div>
      <div class="flex justify-between gap-2">
        <div class="flex justify-start flex-wrap gap-2 align-items-center">
          <template v-for="(value, idx) in getJobNameCandidateNameOpeningNames">
            <div :key="value" class="flex gap-1.5">
              <div class="rounded-2xl w-1 h-1 m-auto" v-if="idx/2!==0" :style="{backgroundColor: 'rgb(4,4,4,0.77)'}"></div>
              <WcTypography :title="value" size="xs" color="rgb(4,4,4,0.77)"/>
            </div>
          </template>
        </div>
        <WcTypography :title="getText" size="xs" color="rgb(4,4,4,0.77)"/>
      </div>
    </div>
    <WcDialog
      :close-sync="showDeleteNotificationDialog"
      :title="t('confirmSingleNotificationDelete')"
      @close-jdialog="handleDeleteNotificationDialog">
      <template #content>
        <div>
          <WcTypography :title="$t('general.thisActionCannotBeUnDone')" size="xs" color="rgb(4,4,4,0.77)"/>
        </div>
      </template>
      <template #footer>
        <div class="flex items-center justify-end" style="gap: 12px;">
          <Button type="text" classes="font-weight-600" @click="handleDeleteNotificationDialog">
            {{$t('general.cancel')}}
          </Button>
          <Button
            type="danger"
            classes="font-weight-600"
            :loading="deleteNotificationLoading"
            @click="deleteNotification"
          >
            {{$t('general.delete')}}
          </Button>
        </div>
      </template>
    </WcDialog>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import WcTypography from '@/components/UILibrary/Typography/WcTypography.vue'
import WcIconPencil from '@/components/UILibrary/Icons/WcIconPencil.vue'
import { InboxEntity } from '@/views/home-page/inbox/models/inbox.entity'
import InboxNotificationsResource from '@/api/inbox/inboxNotifications'
import {
  getIconConfig,
  IconConfig,
  InboxTypeEnum,
  NotificationIconType
} from '@/views/home-page/inbox/models/inbox-type.enum'
import WcIconCalendarCircle from '@/components/UILibrary/Icons/WcIconCalendarCircle.vue'
import WcIconDescription from '@/components/UILibrary/Icons/WcIconDescription.vue'
import { getTimeDifferenceText } from '@/utils/date'
import { APP_DOMAIN } from '@/utils/constants'
import WcIconEmail from '@/components/UILibrary/Icons/WcIconEmail.vue'
import WcIconSuitcase from '@/components/UILibrary/Icons/WcIconSuitcase.vue'
import WcIconUser from '@/components/UILibrary/Icons/WcIconUser.vue'
import WcIconBin from '@/components/UILibrary/Icons/WcIconBin.vue'
import WcIconOffer from '@/components/UILibrary/Icons/WcIconOffer.vue'
import WcIconStar from '@/components/UILibrary/Icons/WcIconStar.vue'
import { handleError } from '@/utils/request'
import WcTooltip from '@/components/UILibrary/WcTooltip.vue'
import WcIconMessage from '@/components/UILibrary/Icons/WcIconMessage.vue'
import WcDialog from '@/components/UILibrary/WcDialog.vue'
import Button from '@/components/UILibrary/Buttons/Button.vue'
import WcIconAddNote from '@/components/UILibrary/Icons/WcIconAddNote.vue'
import WcIconNotes from '@/components/UILibrary/Icons/WcIconNotes.vue'
import { createFilterWidthMap } from '@/utils/create-filter-width-map'

@Component({
  name: 'InboxNotification',
  components: { WcIconNotes, WcIconAddNote, Button, WcDialog, WcIconMessage, WcTooltip, WcIconStar, WcIconOffer, WcIconBin, WcIconUser, WcIconSuitcase, WcIconEmail, WcIconDescription, WcIconCalendarCircle, WcIconPencil, WcTypography }
})
export default class InboxNotification extends Vue {
  @Prop({ required: true }) inboxNotification!: InboxEntity
  inboxNotificationsResource = new InboxNotificationsResource()
  baseUrl = APP_DOMAIN
  deleteNotificationLoading = false
  showDeleteNotificationDialog = false

  get getText(): string {
    return getTimeDifferenceText(this.inboxNotification.createdAt, new Date())
  }

  notificationIconMap: Record<string, string[]> = {
    [NotificationIconType.WC_ICON_USER]: [InboxTypeEnum.CANDIDATE_OWNER_ASSIGNED, InboxTypeEnum.REQUISITION_RECRUITER_ASSIGNED, InboxTypeEnum.REQUISITION_RECRUITER_REMOVED, InboxTypeEnum.BULK_CANDIDATE_OWNER_ASSIGNED_REMOVED],
    [NotificationIconType.WC_ICON_SUITCASE]: [InboxTypeEnum.JOB_OWNER_COLLABORATOR_ASSIGNED_REMOVED, InboxTypeEnum.APPROVAL_REQUEST, InboxTypeEnum.REQUISITION_APPROVED, InboxTypeEnum.REQUISITION_REJECTED],
    [NotificationIconType.WC_ICON_NOTES]: [InboxTypeEnum.INTERVIEW_SCORES],
    [NotificationIconType.WC_ICON_EMAIL]: [InboxTypeEnum.EMAIL_SENT],
    [NotificationIconType.WC_ICON_OFFER]: [InboxTypeEnum.OFFER_ACCEPTED, InboxTypeEnum.OFFER_REJECTED],
    [NotificationIconType.WC_ICON_PENCIL]: [InboxTypeEnum.MENTIONS],
    [NotificationIconType.WC_ICON_CALENDAR_CIRCLE]: [InboxTypeEnum.INTERVIEWS],
    [NotificationIconType.WC_ICON_ADD_NOTE]: [InboxTypeEnum.MENTIONS_REPLY, InboxTypeEnum.NOTES, InboxTypeEnum.BULK_NOTES, InboxTypeEnum.BULK_MENTIONS]
  };

  notificationIconMapByType = createFilterWidthMap(this.notificationIconMap)

  getNotificationIcon(type: InboxTypeEnum): IconConfig {
    const iconType = this.notificationIconMapByType[type] || ''
    return getIconConfig[iconType] || { component: '' }
  }

  async redirectTo(): Promise<void> {
    if (!this.inboxNotification.isRead) {
      await this.inboxNotificationsResource.update(this.inboxNotification.id, { isRead: true })
    }
    if (!this.inboxNotification.isRead) {
      this.inboxNotification.isRead = true
      this.$emit('read', this.inboxNotification.isRead)
    }
    if (this.inboxNotification.jobApplicationId) {
      const candidateProfileLink = this.getRedirectionLink()
      window.open(candidateProfileLink)
    } else if ([InboxTypeEnum.APPROVAL_REQUEST, InboxTypeEnum.REQUISITION_APPROVED, InboxTypeEnum.REQUISITION_REJECTED].includes(this.inboxNotification.type)) {
      const requisitionTabUrl = this.getRedirectionLink()
      window.open(requisitionTabUrl, '_blank')
    } else if ([InboxTypeEnum.REQUISITION_RECRUITER_ASSIGNED, InboxTypeEnum.REQUISITION_RECRUITER_REMOVED].includes(this.inboxNotification.type)) {
      const viewRequisitionLink = this.getRedirectionLink()
      window.open(viewRequisitionLink, '_blank')
    }
  }

  async deleteNotification(): Promise<void> {
    this.deleteNotificationLoading = true
    try {
      await this.inboxNotificationsResource.destroy(this.inboxNotification.id)
      this.$emit('deleted', this.inboxNotification.id)
      this.$message.success(this.t('successMessages.deleteNotification'))
    } catch (e) {
      handleError(e, this.$message)
    } finally {
      this.deleteNotificationLoading = false
    }
  }

  async handleImportant(): Promise<void> {
    try {
      this.inboxNotification.isImportant = !this.inboxNotification.isImportant
      await this.inboxNotificationsResource.update(this.inboxNotification.id, { isImportant: this.inboxNotification.isImportant })
    } catch (e) {
      handleError(e, this.$message)
    }
  }

  async markAsReadUnread(): Promise<void> {
    try {
      this.inboxNotification.isRead = !this.inboxNotification.isRead
      await this.inboxNotificationsResource.update(this.inboxNotification.id, { isRead: this.inboxNotification.isRead })
      this.$emit('read', this.inboxNotification.isRead)
    } catch (e) {
      handleError(e, this.$message)
    }
  }

  getRedirectionLink(): string {
    const url = `${this.baseUrl}/recruiter/candidate-profile/${this.inboxNotification.jobApplicationId}`
    if (this.inboxNotification.redirectionData.commentId) {
      return `${url}?tabId=${this.inboxNotification.redirectionData.commentId}`
    } else if (this.inboxNotification.redirectionData.bamId) {
      return `${url}?tabId=${this.inboxNotification.redirectionData.bamId}`
    } else if (this.inboxNotification.redirectionData.emailSentId) {
      return `${url}?tabId=${this.inboxNotification.redirectionData.emailSentId}`
    } else if (this.inboxNotification.redirectionData.requisitionId) {
      const requisitionsUrl = `${this.baseUrl}/recruiter/hiring-plan`
      return `${requisitionsUrl}/opening/view/${this.inboxNotification.redirectionData.requisitionId}`
    } else if (this.inboxNotification.redirectionData.filterId) {
      return `${url}?filterId=${this.inboxNotification.redirectionData.filterId}`
    }
    return url
  }

  handleDeleteNotificationDialog(): void {
    this.showDeleteNotificationDialog = !this.showDeleteNotificationDialog
  }

  get getHighlightColor(): string {
    return this.inboxNotification.isRead ? 'rgba(4, 4, 16, 0.38)' : 'rgba(4, 4, 16, 0.87)'
  }

  get getStarredText(): string {
    if (this.inboxNotification.isImportant) {
      return this.t('removeStarred')
    }
    return this.t('markAsStarred')
  }

  get getJobNameCandidateNameOpeningNames(): string[] {
    const values: string[] = []
    if (this.inboxNotification.candidateName) {
      values.push(this.inboxNotification.candidateName)
    }
    if (this.inboxNotification.roleName) {
      values.push(this.inboxNotification.roleName)
    }
    if (this.inboxNotification.redirectionData.openingId && this.inboxNotification.redirectionData.jobName) {
      values.push(`${this.inboxNotification.redirectionData.jobName}`)
      values.push(`ID ${this.inboxNotification.redirectionData.openingId}`)
    }
    return values
  }

  get getInboxReadText(): string {
    if (this.inboxNotification.isRead) {
      return this.t('markAsUnread')
    }
    return this.t('markAsRead')
  }

  t(key: string): string {
    return this.$t(`homePage.inbox.${key}`).toString()
  }
}
</script>

<style scoped>
</style>
